import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'
import type { CustomerSnapshot } from '../../types'

/**
 * AF-126 — compact customer card in the action rail. Risk score gets a
 * 30-day inline sparkline so the analyst can see trajectory without leaving.
 */
export function CustomerSnapshotCard({ customer }: { customer: CustomerSnapshot }) {
  // The alert handler falls back to uuid.Nil when the customer FK
  // doesn't resolve (deleted customer / stale demo data). Don't
  // render a deep-link in that case — the 360 page would just show
  // "Couldn't load customer".
  const hasResolvableId =
    customer.id && customer.id !== '00000000-0000-0000-0000-000000000000'
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-fg">
          Customer
        </p>
        {hasResolvableId ? (
          <Link
            to={`/customers/${customer.id}`}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            360 view
            <ExternalLink size={10} />
          </Link>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="font-medium">{customer.display_name}</p>
        <p className="font-mono text-xs text-muted-fg">{customer.customer_number}</p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-fg">Risk</span>
          <span
            className={cn(
              'font-mono tabular-nums text-sm font-semibold',
              riskColor(customer.risk_score),
            )}
          >
            {customer.risk_score ?? '—'}
          </span>
        </div>
        <Sparkline data={customer.risk_score_history} />
      </div>
      <div className="flex flex-wrap gap-1.5 border-t border-border pt-2">
        <Badge variant="default" className="text-[0.625rem] uppercase">
          {customer.kyc_tier.replace('_', ' ')}
        </Badge>
        {customer.pep && <Badge variant="critical" className="text-[0.625rem]">PEP</Badge>}
        {customer.bvn_masked && (
          <span className="font-mono text-[0.625rem] text-muted-fg">
            BVN {customer.bvn_masked}
          </span>
        )}
      </div>
    </div>
  )
}

function riskColor(score: number | null): string {
  if (score === null) return 'text-muted-fg'
  if (score >= 70) return 'text-status-failed'
  if (score >= 50) return 'text-status-pending'
  return 'text-status-success'
}

function Sparkline({ data }: { data: readonly number[] }) {
  if (data.length === 0) return null
  const w = 80
  const h = 20
  const min = Math.min(...data)
  const max = Math.max(...data)
  const span = Math.max(1, max - min)
  const step = w / Math.max(1, data.length - 1)
  const points = data
    .map((v, i) => {
      const x = i * step
      const y = h - ((v - min) / span) * h
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-label="Risk score, last 30 days"
      className="text-muted-fg"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}
